import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import UserDetailSheet from "@/components/UserDetailSheet";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Filter, Search, X, Coffee, Plane, HeartPulse,
  GraduationCap, Sun, Ban, Save, Trash2, MapPin, Users as UsersIcon,
} from "lucide-react";

const WEEKDAY_KEYS = ["mo", "di", "mi", "do", "fr", "sa", "so"];
const WEEKDAY_LABELS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];
const POSITIONS = ["Apotheker/in", "PTA", "PKA", "Botenfahrer", "Reinigungskraft", "Student", "Praktikant", "Filialleiter"];
const ABS_META = {
  vacation: { label: "Urlaub", icon: Plane },
  sick: { label: "Krank", icon: HeartPulse },
  training: { label: "Fortbildung", icon: GraduationCap },
  special: { label: "Sonderurlaub", icon: Sun },
  maternity: { label: "Elternzeit", icon: Sun },
};

const startOfWeek = (d) => {
  const day = d.getDay() || 7;
  const nd = new Date(d);
  if (day !== 1) nd.setDate(d.getDate() - day + 1);
  nd.setHours(0, 0, 0, 0);
  return nd;
};
const iso = (d) => d.toISOString().slice(0, 10);
const fmtDelta = (mins) => {
  const sign = mins >= 0 ? "+" : "-";
  const abs = Math.abs(Math.round(mins));
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
};
const fmtHM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(Math.round(mins) % 60).padStart(2, "0")}`;

const emptyForm = {
  mode: "work",
  clock_in: "08:00",
  clock_out: "16:00",
  break_minutes: 30,
  location_id: "",
  absence_type: "vacation",
  reason: "",
};

export default function PersonnelPage() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState([]);
  const [posFilter, setPosFilter] = useState([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [expandedLoc, setExpandedLoc] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editCtx, setEditCtx] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const days = useMemo(() => Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d;
  }), [weekStart]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    const q = new URLSearchParams({ from_date: iso(days[0]), to_date: iso(days[6]) });
    if (debounced) q.set("search", debounced);
    const [r, l] = await Promise.all([api.get(`/personnel/plan?${q}`), api.get("/locations")]);
    setData(r.data);
    setLocations(l.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart, debounced]);

  useEffect(() => {
    if (locations.length && Object.keys(expandedLoc).length === 0) {
      setExpandedLoc(Object.fromEntries(locations.map(l => [l.id, true])));
    }
    // eslint-disable-next-line
  }, [locations]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const filtered = data.rows.filter(r => {
      if (posFilter.length && !posFilter.includes(r.position)) return false;
      return true;
    });
    const map = new Map();
    for (const row of filtered) {
      // Get user's primary location from locations (row has no primary_location_id currently). Use first shift/entry loc, or "unassigned"
      const userLocId = row.days.flatMap(d => (d.entries || []).map(e => e.location_id).concat((d.shifts || []).map(s => s.location_id))).find(Boolean) || "unassigned";
      if (locFilter.length && !locFilter.includes(userLocId)) continue;
      if (!map.has(userLocId)) map.set(userLocId, {});
      const posMap = map.get(userLocId);
      const pos = row.position || "Ohne Position";
      if (!posMap[pos]) posMap[pos] = [];
      posMap[pos].push(row);
    }
    const out = [];
    for (const [locId, posMap] of map.entries()) {
      const location = locations.find(l => l.id === locId) || { id: locId, name: "Ohne Standort", color: "#94a3b8" };
      const positions = Object.entries(posMap)
        .map(([name, arr]) => ({ name, users: arr.sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "")) }))
        .sort((a, b) => a.name.localeCompare(b.name));
      out.push({ location, positions });
    }
    return out.sort((a, b) => a.location.name.localeCompare(b.location.name));
  }, [data, locations, locFilter, posFilter]);

  const openEmployee = (userId) => { setSelectedUserId(userId); setDetailOpen(true); };

  const openEdit = (row, day) => {
    const entry = day.entries?.[0];
    const abs = day.absences?.[0];
    setEditCtx({ row, day });
    if (abs) {
      setForm({ ...emptyForm, mode: "absence", absence_type: abs.type || "vacation", reason: abs.reason || "", location_id: locations[0]?.id || "" });
    } else if (entry) {
      setForm({
        mode: "work",
        clock_in: entry.clock_in?.slice(11, 16) || "08:00",
        clock_out: entry.clock_out?.slice(11, 16) || "16:00",
        break_minutes: entry.break_minutes ?? 30,
        location_id: entry.location_id || locations[0]?.id || "",
        absence_type: "vacation", reason: "",
      });
    } else {
      setForm({ ...emptyForm, location_id: locations[0]?.id || "" });
    }
  };

  const saveEdit = async () => {
    if (!editCtx) return;
    setSaving(true);
    try {
      await api.post("/personnel/day-entry", {
        user_id: editCtx.row.user_id, date: editCtx.day.date, mode: form.mode,
        clock_in: form.clock_in, clock_out: form.clock_out,
        break_minutes: parseFloat(form.break_minutes) || 0,
        location_id: form.location_id || null,
        absence_type: form.absence_type, reason: form.reason,
      });
      toast.success("Gespeichert");
      setEditCtx(null); setForm(emptyForm); await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const deleteDay = async () => {
    if (!editCtx || !window.confirm("Wirklich alle Einträge dieses Tages löschen?")) return;
    setSaving(true);
    try {
      await api.post("/personnel/day-entry", { user_id: editCtx.row.user_id, date: editCtx.day.date, mode: "delete" });
      toast.success("Gelöscht");
      setEditCtx(null); await load();
    } catch (err) { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  const clearFilters = () => { setLocFilter([]); setPosFilter([]); setSearch(""); };
  const activeFilters = locFilter.length + posFilter.length + (debounced ? 1 : 0);
  const monthLabel = days[3].toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  return (
    <div className="space-y-4" data-testid="personnel-root">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <div className="overline">Verwaltung</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold capitalize">{monthLabel}</h2>
          <p className="text-sm text-slate-500 mt-1">Personaleinsatzplan – Soll vs. Ist. Klicke auf einen Namen zum Verwalten oder auf eine Zelle zum Bearbeiten.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="personnel-search" placeholder="Mitarbeiter…" className="pl-9 w-52" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button data-testid="personnel-filter-btn" variant="outline" className="gap-2">
                <Filter className="w-4 h-4" /> Filter
                {activeFilters > 0 && <Badge variant="secondary" className="ml-1">{activeFilters}</Badge>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-4" align="end">
              <div>
                <div className="text-xs font-heading font-bold uppercase tracking-wider text-slate-500 mb-2">Standorte</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {locations.map(l => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={locFilter.includes(l.id)} onCheckedChange={(v) => setLocFilter(v ? [...locFilter, l.id] : locFilter.filter(x => x !== l.id))} />
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
                      {l.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-heading font-bold uppercase tracking-wider text-slate-500 mb-2">Berufe</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {POSITIONS.map(p => (
                    <label key={p} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={posFilter.includes(p)} onCheckedChange={(v) => setPosFilter(v ? [...posFilter, p] : posFilter.filter(x => x !== p))} />
                      {p}
                    </label>
                  ))}
                </div>
              </div>
              {activeFilters > 0 && <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full gap-2"><X className="w-3.5 h-3.5" /> Zurücksetzen</Button>}
            </PopoverContent>
          </Popover>
          <Button data-testid="personnel-prev" variant="outline" size="icon" onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * 86400000))}><ChevronLeft className="w-4 h-4" /></Button>
          <Button data-testid="personnel-today" variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))}>Heute</Button>
          <Button data-testid="personnel-next" variant="outline" size="icon" onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * 86400000))}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Card className="border-border/60 overflow-x-auto">
        <CardContent className="p-0">
          <div className="min-w-[900px]">
            {/* Header row */}
            <div className="grid grid-cols-[minmax(260px,1fr)_repeat(7,minmax(120px,1fr))] border-b border-border/60 bg-slate-50 dark:bg-slate-900 sticky top-0 z-10">
              <div className="px-4 py-3 flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider text-slate-500">
                <MapPin className="w-3.5 h-3.5" /> Standorte
              </div>
              {days.map((d, i) => {
                const isToday = iso(d) === iso(new Date());
                return (
                  <div key={i} className={`px-3 py-3 text-center border-l border-border/60 ${isToday ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}>
                    <div className={`text-xs ${isToday ? "text-blue-700 dark:text-blue-400 font-bold" : "text-slate-500"}`}>{WEEKDAY_LABELS[i]}</div>
                    <div className={`font-heading text-2xl font-bold ${isToday ? "text-blue-700 dark:text-blue-400" : ""}`}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>

            {grouped.length === 0 && (
              <div className="text-center text-slate-500 py-16 text-sm">Keine Mitarbeiter für den gewählten Filter.</div>
            )}
            {grouped.map(({ location, positions }) => (
              <div key={location.id}>
                <button
                  data-testid={`personnel-loc-${location.id}`}
                  onClick={() => setExpandedLoc({ ...expandedLoc, [location.id]: !expandedLoc[location.id] })}
                  className="w-full grid grid-cols-[minmax(260px,1fr)_repeat(7,minmax(120px,1fr))] items-center text-left border-b-2 hover:brightness-95 transition"
                  style={{ backgroundColor: `${location.color}22`, borderColor: location.color }}
                >
                  <div className="px-4 py-2 font-heading font-bold flex items-center gap-2" style={{ color: location.color }}>
                    <span>{expandedLoc[location.id] === false ? "▶" : "▼"}</span>{location.name}
                  </div>
                  <div className="col-span-7" />
                </button>

                {expandedLoc[location.id] !== false && positions.map(({ name, users: posUsers }) => (
                  <React.Fragment key={`${location.id}_${name}`}>
                    <div className="grid grid-cols-[minmax(260px,1fr)_repeat(7,minmax(120px,1fr))] bg-slate-50/50 dark:bg-slate-900/40 border-b border-border/40">
                      <div className="px-4 py-1.5 text-xs font-bold text-slate-500 flex items-center gap-1.5">
                        <UsersIcon className="w-3 h-3" /> {name}
                      </div>
                      <div className="col-span-7" />
                    </div>
                    {posUsers.map(row => {
                      const weekActual = row.days.reduce((s, d) => s + (d.actual_hours || 0), 0) * 60;
                      const weekTarget = row.days.reduce((s, d) => s + (d.target_hours || 0), 0) * 60;
                      const delta = weekActual - weekTarget;
                      const isPositive = delta >= 0;
                      return (
                        <div key={row.user_id} data-testid={`personnel-row-${row.user_id}`} className="grid grid-cols-[minmax(260px,1fr)_repeat(7,minmax(120px,1fr))] border-b border-border/40 hover:bg-slate-50/40 dark:hover:bg-slate-900/30">
                          <button
                            data-testid={`personnel-name-${row.user_id}`}
                            onClick={() => openEmployee(row.user_id)}
                            className="text-left px-4 py-3 hover:bg-blue-50/60 dark:hover:bg-blue-950/20 transition-colors"
                            title="Klicken für Details"
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <div className="font-semibold truncate underline-offset-2 hover:underline">{row.last_name}, {row.first_name}</div>
                              <div className={`text-xs font-bold tabular-nums ${isPositive ? "text-blue-700 dark:text-blue-400" : "text-rose-600"}`}>{fmtDelta(delta)}</div>
                            </div>
                            <div className="flex items-baseline justify-between gap-2 mt-0.5">
                              <div className="text-xs text-slate-500">{row.position}</div>
                              <div className="text-[10px] text-slate-500 tabular-nums">{fmtHM(weekActual)} / {fmtHM(weekTarget)}h</div>
                            </div>
                          </button>
                          {row.days.map((d) => {
                            const abs = d.absences?.[0];
                            const entry = d.entries?.[0];
                            const has = !!entry;
                            const overtime = d.overtime_hours || 0;
                            return (
                              <div
                                key={d.date}
                                data-testid={`personnel-cell-${row.user_id}-${d.date}`}
                                onClick={() => openEdit(row, d)}
                                className={`border-l border-border/40 min-h-[68px] p-1.5 cursor-pointer transition-colors hover:bg-blue-50/40 dark:hover:bg-blue-950/20`}
                              >
                                {abs ? (
                                  <div className="h-full min-h-[52px] rounded-md flex flex-col items-center justify-center gap-0.5 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                    {React.createElement(ABS_META[abs.type]?.icon || Plane, { className: "w-3.5 h-3.5" })}
                                    <span className="text-[10px] font-medium">{ABS_META[abs.type]?.label || abs.type}</span>
                                  </div>
                                ) : has ? (
                                  <div className="rounded-md px-2 py-1.5 text-xs" style={{ backgroundColor: `${location.color}22`, borderLeft: `3px solid ${location.color}` }}>
                                    <div className="flex justify-between items-baseline">
                                      <div className="font-semibold tabular-nums">{entry.clock_in?.slice(11,16)} – {entry.clock_out?.slice(11,16) || "…"}</div>
                                      <div className="text-[10px] text-slate-500 tabular-nums">{d.actual_hours.toFixed(1)}h</div>
                                    </div>
                                    <div className="w-full h-0.5 rounded-full mt-1" style={{ backgroundColor: location.color }} />
                                    {overtime > 0 && <div className="text-[10px] text-amber-700 font-semibold mt-0.5">+{overtime.toFixed(1)}h Ü.</div>}
                                    {entry.break_minutes > 0 && <div className="text-[10px] text-slate-500 mt-0.5">Pause {entry.break_minutes}m</div>}
                                  </div>
                                ) : d.target_hours > 0 ? (
                                  <div className="h-full min-h-[52px] rounded-md border border-dashed border-border/40 flex items-center justify-center text-[10px] text-slate-400">
                                    <div className="text-center">
                                      <div>Ziel {d.target_hours}h</div>
                                      <div className="text-slate-300">+</div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="h-full min-h-[52px] flex items-center justify-center text-[10px] text-slate-300 dark:text-slate-700">frei</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit dialog with 3 modes */}
      <Dialog open={!!editCtx} onOpenChange={(o) => { if (!o) { setEditCtx(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editCtx?.row?.name}
              <span className="ml-2 text-sm font-normal text-slate-500">{editCtx?.day?.date && new Date(editCtx.day.date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</span>
            </DialogTitle>
            <DialogDescription>Änderung wird ins Audit-Log geschrieben. Auch vergangene Tage können bearbeitet werden.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Modus</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Button variant={form.mode === "work" ? "default" : "outline"} size="sm" data-testid="mode-work" onClick={() => setForm({ ...form, mode: "work" })}><Coffee className="w-3.5 h-3.5 mr-1" /> Arbeitszeit</Button>
                <Button variant={form.mode === "absence" ? "default" : "outline"} size="sm" data-testid="mode-absence" onClick={() => setForm({ ...form, mode: "absence" })}><Plane className="w-3.5 h-3.5 mr-1" /> Abwesenheit</Button>
                <Button variant={form.mode === "free" ? "default" : "outline"} size="sm" data-testid="mode-free" onClick={() => setForm({ ...form, mode: "free" })}><Ban className="w-3.5 h-3.5 mr-1" /> Frei</Button>
              </div>
            </div>

            {form.mode === "work" && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Beginn</Label><Input type="time" data-testid="edit-clock-in" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} /></div>
                  <div><Label>Ende</Label><Input type="time" data-testid="edit-clock-out" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Pause (Min.)</Label><Input type="number" min={0} step={5} data-testid="edit-break" value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} /></div>
                  <div>
                    <Label>Standort</Label>
                    <Select value={form.location_id || ""} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                      <SelectTrigger data-testid="edit-location"><SelectValue placeholder="Standort" /></SelectTrigger>
                      <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="text-xs text-slate-500">Soll: {editCtx?.day?.target_hours}h · Ist: {editCtx?.day?.actual_hours}h</div>
              </div>
            )}

            {form.mode === "absence" && (
              <div className="space-y-3">
                <div>
                  <Label>Art der Abwesenheit</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {Object.entries(ABS_META).map(([id, m]) => (
                      <Button key={id} variant={form.absence_type === id ? "default" : "outline"} size="sm" onClick={() => setForm({ ...form, absence_type: id })} data-testid={`absence-type-${id}`}>
                        <m.icon className="w-3.5 h-3.5 mr-1" /> {m.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div><Label>Notiz</Label><Input data-testid="edit-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="optional" /></div>
              </div>
            )}

            {form.mode === "free" && (
              <div className="text-sm text-slate-500 p-3 rounded-md bg-slate-50 dark:bg-slate-900/40 border border-border/60">
                Der Tag wird als freier Tag markiert. Alle Zeiterfassungen und Abwesenheiten dieses Tages werden entfernt.
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="ghost" onClick={deleteDay} data-testid="personnel-delete" className="text-rose-600 hover:text-rose-700 sm:mr-auto"><Trash2 className="w-4 h-4 mr-1" /> Tag löschen</Button>
            <Button variant="outline" onClick={() => setEditCtx(null)}>Abbrechen</Button>
            <Button data-testid="personnel-save" onClick={saveEdit} disabled={saving}><Save className="w-4 h-4 mr-2" /> {saving ? "Speichert…" : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee detail sheet */}
      <UserDetailSheet
        userId={selectedUserId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        locations={locations}
        onChanged={load}
      />
    </div>
  );
}
